import { HOME } from "@/constants/testIds";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Mail, Instagram, Facebook, Linkedin, Youtube, Twitter, Send, QrCode, Sparkles } from "lucide-react";

export function AboutDigiswing() {
  return (
    <section id="about" className="py-20 md:py-28">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
          About DIGISWING
        </div>
        <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
          DIGISWING Innovation <br />
          <span className="brand-gradient-text">Private Limited.</span>
        </h2>
        <p className="mt-6 text-lg text-[#4A4846] leading-relaxed">
          DIGISWING Innovation Private Limited is an Indian technology company focused on building
          digital products that solve real-world problems. Time2Cash is our flagship platform, designed
          to strengthen local communities by connecting people, businesses, opportunities, and services
          through technology.
        </p>
        <p className="mt-4 text-lg text-[#4A4846] leading-relaxed">
          Our vision is to build practical, scalable solutions that create value for individuals, businesses, and cities.
        </p>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "What is Time2Cash?",
    a: "Time2Cash is a hyperlocal community platform that connects people with local tasks, jobs, businesses, marketplace listings, blood requests, and essential city services—all in one app.",
  },
  {
    q: "How do I post a local task or job?",
    a: "Simply create an account, choose the appropriate category, add your requirements, budget, and location, then publish your post. Nearby users can discover and respond to it.",
  },
  {
    q: "How does the Wallet & Credits system work?",
    a: "Time2Cash uses a simple credits system for selected premium actions like posting tasks, unlocking opportunities, and accessing premium features. Credits can be purchased and managed directly from your wallet.",
  },
  {
    q: "How do Business Plans work?",
    a: "Businesses can register their shop and choose from Free, Lite, or Pro+ plans to unlock additional features such as verified profiles, analytics, premium visibility, and enhanced business tools.",
  },
  {
    q: "When will the SOS Safety feature be available?",
    a: "The Emergency SOS Safety feature is planned for release in 2027. It will allow users to instantly share their live location with trusted contacts during emergencies.",
  },
  {
    q: "How can I contact the Time2Cash team?",
    a: "You can reach our team through the Contact Us page, WhatsApp support, or by emailing hello@time2cash.co. We're always happy to help with your questions and feedback.",
  },
];

export function FAQ() {
  return (
    <section id="faq" className="py-20 md:py-28 bg-[#FFF9F5]">
      <div className="container mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-1.5 text-xs font-semibold text-[#4A4846]">
            FAQ
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
            Common questions.
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid={HOME.faqAccordion}>
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="rounded-2xl border border-[#EBE8E3] bg-white px-5 data-[state=open]:border-[#FF5A00] transition-colors"
            >
              <AccordionTrigger className="text-left font-bold text-[#1A1918] hover:no-underline text-base">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#4A4846] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 text-center">
          <a
            href="contact"
            data-testid={HOME.faqViewAll}
            className="inline-flex items-center gap-2 text-[#FF5A00] font-semibold hover:gap-3 transition-all"
          >
            Have more questions? Contact us <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export function DownloadSection() {
  return (
    <section id="download" className="py-20 md:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#FF5A00] via-[#FF7A2A] to-[#FF9A55] text-white p-8 md:p-14 grain-overlay">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-black/10 blur-2xl" />

          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/20 text-white px-4 py-1.5 text-xs font-semibold">
                <Sparkles className="w-3.5 h-3.5" /> Ready to get started?
              </div>
              <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">
                Download Time2Cash.
                <br />
                <span className="text-white/85">Experience everything local.</span>
              </h2>
              <p className="mt-4 text-lg text-white/85 max-w-lg">
                Free to download. Free to use. Just your city, one tap away.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#"
                  data-testid={HOME.downloadAndroid}
                  className="inline-flex items-center gap-3 rounded-2xl bg-[#1A1918] hover:bg-black text-white pl-4 pr-5 py-3 transition-colors"
                >
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.6 20.5 14 12 3.6 3.5c-.3.2-.6.7-.6 1.3v14.4c0 .6.3 1.1.6 1.3z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] opacity-80">GET IT ON</div>
                    <div className="text-sm font-bold">Google Play</div>
                  </div>
                </a>
                <button
                  type="button"
                  data-testid={HOME.downloadIphone}
                  disabled
                  aria-disabled="true"
                  className="inline-flex items-center gap-3 rounded-2xl bg-white/15 border border-white/20 text-white/70 pl-4 pr-5 py-3 cursor-not-allowed"
                >
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.4 13.1c0-2.6 2.1-3.9 2.2-4-1.2-1.7-3-2-3.7-2-1.6-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.1 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8 1.5 0 2 .8 3.3.8 1.4 0 2.3-1.2 3.2-2.4 1-1.4 1.4-2.7 1.4-2.8-.1 0-2.8-1-2.8-4zM14.1 5.3c.7-.9 1.2-2.1 1-3.3-1 0-2.3.7-3 1.5-.7.8-1.3 2-1.1 3.1 1.1.1 2.3-.5 3.1-1.3z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px]">Coming Soon</div>
                    <div className="text-sm font-bold">App Store</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="bg-white text-[#1A1918] rounded-3xl p-6 shadow-2xl">
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl bg-[#FDFBF7] border-2 border-[#EBE8E3] flex items-center justify-center">
                  <QrCode className="w-28 h-28 md:w-36 md:h-36 text-[#1A1918]" strokeWidth={1.25} />
                </div>
                <div className="mt-4 text-center">
                  <div className="text-sm font-bold">Scan to download</div>
                  <div className="text-xs text-[#78716C]">Point your camera here</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const socials = [
  { icon: Instagram, l: "Instagram", h: "https://instagram.com" },
  { icon: Facebook, l: "Facebook", h: "https://facebook.com" },
  { icon: Linkedin, l: "LinkedIn", h: "https://linkedin.com" },
  { icon: Youtube, l: "YouTube", h: "https://youtube.com" },
  { icon: Twitter, l: "X (Twitter)", h: "https://twitter.com" },
  { icon: Send, l: "Telegram", h: "https://telegram.org" },
];

export function ContactSection() {
  return (
    <section id="contact" className="py-20 md:py-28 bg-[#FFF9F5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-1.5 text-xs font-semibold text-[#4A4846]">
              Contact Us
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
              Say hi to our team.
            </h2>
            <p className="mt-4 text-lg text-[#4A4846]">
              Whether it&apos;s support, partnerships, or a hello — we&apos;re one email away.
            </p>
            <div className="mt-8 space-y-3">
              <div className="text-sm text-[#78716C]">Company</div>
              <div className="text-lg font-bold text-[#1A1918]">DIGISWING Innovation Private Limited</div>
              <div className="text-sm text-[#78716C]">Product</div>
              <div className="text-lg font-bold text-[#FF5A00]">Time2Cash</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:help@time2cash.co"
              data-testid={HOME.contactEmailSupport}
              className="rounded-3xl bg-white border border-[#EBE8E3] p-6 hover:border-[#FF5A00] transition"
            >
              <Mail className="w-6 h-6 text-[#FF5A00]" />
              <div className="mt-3 text-xs font-semibold text-[#78716C]">Support</div>
              <div className="text-base font-bold text-[#1A1918] break-all">help@time2cash.co</div>
            </a>
            <a
              href="mailto:hello@time2cash.co"
              data-testid={HOME.contactEmailGeneral}
              className="rounded-3xl bg-white border border-[#EBE8E3] p-6 hover:border-[#FF5A00] transition"
            >
              <Mail className="w-6 h-6 text-[#FF5A00]" />
              <div className="mt-3 text-xs font-semibold text-[#78716C]">General</div>
              <div className="text-base font-bold text-[#1A1918] break-all">hello@time2cash.co</div>
            </a>
            <a
              href="mailto:partners@time2cash.co"
              data-testid={HOME.contactEmailPartners}
              className="rounded-3xl bg-white border border-[#EBE8E3] p-6 hover:border-[#FF5A00] transition sm:col-span-2"
            >
              <Mail className="w-6 h-6 text-[#FF5A00]" />
              <div className="mt-3 text-xs font-semibold text-[#78716C]">Business Partnerships</div>
              <div className="text-base font-bold text-[#1A1918] break-all">partners@time2cash.co</div>
            </a>

            <div className="sm:col-span-2">
              <div className="text-xs font-semibold text-[#78716C] mb-3">Follow us</div>
              <div className="flex flex-wrap gap-2">
                {socials.map(({ icon: Icon, l, h }) => (
                  <a
                    key={l}
                    href={h}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EBE8E3] px-4 py-2 text-sm font-medium hover:border-[#FF5A00] hover:text-[#FF5A00] transition"
                  >
                    <Icon className="w-4 h-4" />
                    {l}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
